import { HELP_REVISIONS } from '../data/helpRevisions';

// Which guides have changed since this person last read them (owner, 2026-10-05: "Whenever manuals
// and guides are updated with the new content then it should flash under User guides menu on that
// particular manual that new version has been made available").
//
// Keyed on the guide's REVISION, a hash of everything the reader sees, written by
// manuals/build-help.mjs. Not on the version printed in the masthead: somebody will correct a guide
// and forget to touch that line, and a badge that depends on remembering is a badge that lies.
//
// Per person per browser, in localStorage. It is a convenience, not a record: if it is lost, the
// worst case is a badge that does not appear, and the guide is a click away regardless. Every read
// and write is guarded, because the accessor itself throws in a private window or with site data
// blocked.
//
// It reads helpRevisions, NOT helpContent. The shell imports this to badge the sidebar, and
// importing the guides here would pull 240 kB of guide text into the bundle everybody downloads to
// sign in, defeating the lazy chunk the Help Centre is split into.

const KEY = (userId) => `clinic_help_seen_${userId || 'me'}`;

const read = (userId) => {
  try {
    return JSON.parse(localStorage.getItem(KEY(userId)) || '{}') || {};
  } catch {
    return {};
  }
};

const write = (userId, value) => {
  try {
    localStorage.setItem(KEY(userId), JSON.stringify(value));
  } catch {
    /* Nothing to do: the badge simply will not persist for this browser. */
  }
};

/**
 * The guides this person has read before and that have changed since.
 *
 * <p>A guide they have NEVER opened is not "updated" and carries no badge. For a new member of
 * staff every guide is new, and flagging all of them says nothing; the badge means one thing only,
 * that something they have already read is not the same any more.
 */
export const updatedGuideIds = (userId) => {
  const seen = read(userId);
  return HELP_REVISIONS.filter((g) => seen[g.id] && seen[g.id] !== g.revision).map((g) => g.id);
};

/** They have just opened it, so this revision is the one they have seen. */
export const markGuideSeen = (userId, guideId) => {
  const guide = HELP_REVISIONS.find((g) => g.id === guideId);
  if (!guide) return;
  write(userId, { ...read(userId), [guideId]: guide.revision });
  // The sidebar badge lives in the shell, which has no reason to re-render on its own.
  window.dispatchEvent(new Event('clinic:help-seen'));
};

/**
 * Everything is as it stands now, without reading any of it. Used once the FIRST time somebody
 * reaches the Help Centre: from then on a change is something they can be told about, and until
 * then there is nothing honest to say.
 */
export const markAllSeenIfFirstVisit = (userId) => {
  const seen = read(userId);
  if (Object.keys(seen).length > 0) return;
  write(userId, Object.fromEntries(HELP_REVISIONS.map((g) => [g.id, g.revision])));
  window.dispatchEvent(new Event('clinic:help-seen'));
};
